import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function ContactsList({ adminToken }) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [notifFilter, setNotifFilter] = useState('enabled'); // 'all', 'enabled', 'disabled'
    const [sexFilter, setSexFilter] = useState(''); // '', 'male', 'female'

    const filteredContacts = contacts.filter(c => {
        // Search filter
        if (searchQuery) {
            const searchStr = searchQuery.toLowerCase();
            const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
            const email = (c.email || '').toLowerCase();
            if (!fullName.includes(searchStr) && !email.includes(searchStr)) return false;
        }
        // Notification filter
        if (notifFilter === 'enabled' && c.notificationsDisabled) return false;
        if (notifFilter === 'disabled' && !c.notificationsDisabled) return false;
        // Sex filter
        if (sexFilter && c.sex !== sexFilter) return false;
        return true;
    });

    useEffect(() => {
        fetchCities();
        fetchContacts();
    }, []);

    const fetchCities = async () => {
        try {
            const res = await axios.get('/speeddate/api/cities', {
                headers: { 'x-admin-token': adminToken }
            });
            setCities(res.data);
        } catch (err) {
            console.error("Error fetching cities", err);
        }
    };

    const fetchContacts = async (cityId = selectedCity) => {
        try {
            setLoading(true);
            const res = await axios.get('/speeddate/api/contacts', {
                params: { cityId },
                headers: { 'x-admin-token': adminToken }
            });
            setContacts(res.data);
            setSelectedEmails([]); // Reset selection on refresh/filter
        } catch (err) {
            console.error("Error fetching contacts", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCityChange = (e) => {
        const cityId = e.target.value;
        setSelectedCity(cityId);
        fetchContacts(cityId);
    };

    const toggleSelectAll = () => {
        if (selectedEmails.length === filteredContacts.length && filteredContacts.length > 0) {
            setSelectedEmails([]);
        } else {
            setSelectedEmails(filteredContacts.map(c => c.email));
        }
    };

    const toggleSelect = (email) => {
        setSelectedEmails(prev => 
            prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
        );
    };

    const handleSendEmail = async () => {
        if (selectedEmails.length === 0) return;

        const { value: formValues } = await window.Swal.fire({
            title: 'Invia Email Personalizzata',
            html:
                '<div class="text-start mb-3"><label class="small text-muted">Destinatari: ' + selectedEmails.length + '</label></div>' +
                '<input id="swal-input-subject" class="form-control mb-3" placeholder="Oggetto">' +
                '<textarea id="swal-input-body" class="form-control" placeholder="Messaggio..." style="height: 200px"></textarea>',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Invia Email',
            cancelButtonText: 'Annulla',
            customClass: {
                popup: 'swal2-glass',
                confirmButton: 'btn btn-primary rounded-pill px-4',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4'
            },
            buttonsStyling: false,
            preConfirm: () => {
                const subject = document.getElementById('swal-input-subject').value;
                const body = document.getElementById('swal-input-body').value;
                if (!subject || !body) {
                    window.Swal.showValidationMessage('Inserisci oggetto e messaggio');
                    return false;
                }
                return { subject, body };
            }
        });

        if (formValues) {
            try {
                window.Swal.fire({
                    title: 'Invio in corso...',
                    text: 'Stiamo inviando le email ai contatti selezionati.',
                    allowOutsideClick: false,
                    didOpen: () => { window.Swal.showLoading(); },
                    customClass: { popup: 'swal2-glass' }
                });

                await axios.post('/speeddate/api/contacts/send-custom-email', {
                    emails: selectedEmails,
                    subject: formValues.subject,
                    body: formValues.body
                });

                window.Swal.fire({
                    title: 'Inviato!',
                    text: 'Le email sono state inviate con successo.',
                    icon: 'success',
                    customClass: { popup: 'swal2-glass' }
                });
                setSelectedEmails([]);
            } catch (err) {
                window.Swal.fire({
                    title: 'Errore',
                    text: 'Si è verificato un errore durante l\'invio.',
                    icon: 'error',
                    customClass: { popup: 'swal2-glass' }
                });
            }
        }
    };

    const handleForgetContact = async (email) => {
        const confirm = await window.Swal.fire({
            title: 'Diritto all\'oblio',
            text: `Sei sicuro di voler eliminare definitivamente tutti i dati per ${email}? Questa azione non è reversibile e rimuoverà l'utente e tutte le sue partecipazioni.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sì, elimina tutto',
            cancelButtonText: 'Annulla',
            customClass: { popup: 'swal2-glass' }
        });

        if (confirm.isConfirmed) {
            try {
                window.Swal.fire({
                    title: 'Eliminazione in corso...',
                    allowOutsideClick: false,
                    didOpen: () => { window.Swal.showLoading(); },
                    customClass: { popup: 'swal2-glass' }
                });

                await axios.post('/speeddate/api/contacts/forget', { email }, {
                    headers: { 'x-admin-token': adminToken }
                });

                window.Swal.fire('Eliminato!', 'I dati sono stati cancellati.', 'success');
                fetchContacts(); // Refresh list
            } catch (err) {
                window.Swal.fire('Errore', 'Si è verificato un errore durante l\'eliminazione.', 'error');
            }
        }
    };

    return (
        <div className="card border-0 shadow-sm glass">
            <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div className="d-flex align-items-center">
                    <h4 className="mb-0 fw-bold"><i className="fas fa-address-book text-primary me-2"></i>Anagrafica Contatti</h4>
                    {selectedEmails.length > 0 && (
                        <button 
                            className="btn btn-primary btn-sm rounded-pill px-4 ms-3 shadow-sm animate__animated animate__fadeIn"
                            onClick={handleSendEmail}
                        >
                            <i className="fas fa-paper-plane me-2"></i>
                            Invia Email a {selectedEmails.length}
                        </button>
                    )}
                </div>
                
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="input-group input-group-sm rounded-pill overflow-hidden shadow-sm border-0" style={{ width: '250px', backgroundColor: '#f8f9fa' }}>
                        <span className="input-group-text bg-transparent border-0 pe-1">
                            <i className="fas fa-search text-muted"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control border-0 bg-transparent shadow-none"
                            placeholder="Cerca nome, cognome o email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="d-flex align-items-center">
                        <label htmlFor="notifFilter" className="small text-muted me-2 mb-0 d-none d-sm-block">Notifiche:</label>
                        <select 
                            id="notifFilter"
                            className="form-select form-select-sm rounded-pill border-0 shadow-sm px-3" 
                            style={{ minWidth: '130px', backgroundColor: '#f8f9fa' }}
                            value={notifFilter}
                            onChange={(e) => setNotifFilter(e.target.value)}
                        >
                            <option value="enabled">Solo attive</option>
                            <option value="disabled">Solo disattivate</option>
                            <option value="all">Tutte</option>
                        </select>
                    </div>
                    <div className="d-flex align-items-center">
                        <label htmlFor="sexFilter" className="small text-muted me-2 mb-0 d-none d-sm-block">Sesso:</label>
                        <select 
                            id="sexFilter"
                            className="form-select form-select-sm rounded-pill border-0 shadow-sm px-3" 
                            style={{ minWidth: '110px', backgroundColor: '#f8f9fa' }}
                            value={sexFilter}
                            onChange={(e) => setSexFilter(e.target.value)}
                        >
                            <option value="">Tutti</option>
                            <option value="male">Uomo</option>
                            <option value="female">Donna</option>
                        </select>
                    </div>
                    <div className="d-flex align-items-center">
                        <label htmlFor="cityFilter" className="small text-muted me-2 mb-0 d-none d-sm-block">Città:</label>
                        <select 
                            id="cityFilter"
                            className="form-select form-select-sm rounded-pill border-0 shadow-sm px-3" 
                            style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}
                            value={selectedCity}
                            onChange={handleCityChange}
                        >
                            <option value="">Tutte le città</option>
                            {cities.map(city => (
                                <option key={city.id} value={city.id}>{city.name}</option>
                            ))}
                        </select>
                    </div>
                    
                    <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={() => fetchContacts()} disabled={loading}>
                        <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>
            </div>
            <div className="card-body px-4">
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0" style={{ width: '40px' }}>
                                    <div className="form-check">
                                        <input 
                                            className="form-check-input" 
                                            type="checkbox" 
                                            checked={filteredContacts.length > 0 && selectedEmails.length === filteredContacts.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </div>
                                </th>
                                <th className="small text-uppercase fw-bold text-muted border-0">Nominativo</th>
                                <th className="small text-uppercase fw-bold text-muted border-0">Contatti</th>
                                <th className="small text-uppercase fw-bold text-muted border-0">Città</th>
                                <th className="small text-uppercase fw-bold text-muted border-0">Social</th>
                                <th className="small text-uppercase fw-bold text-muted border-0 text-center">Notifiche</th>
                                <th className="small text-uppercase fw-bold text-muted border-0 text-center">Partecipazioni</th>
                                <th className="small text-uppercase fw-bold text-muted border-0 text-center">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredContacts.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        {loading ? (
                                            <div className="spinner-border text-primary" role="status"></div>
                                        ) : (
                                            <div className="text-muted">
                                                <i className="fas fa-user-slash fa-3x mb-3 opacity-25"></i>
                                                <p className="mb-0">Nessun contatto trovato</p>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ) : filteredContacts.map((c, idx) => (
                                <tr key={idx} className={selectedEmails.includes(c.email) ? 'table-primary bg-opacity-10' : ''}>
                                    <td>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={selectedEmails.includes(c.email)}
                                                onChange={() => toggleSelect(c.email)}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div className="bg-primary text-white rounded-circle p-2 me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                                                <i className="fas fa-user"></i>
                                            </div>
                                            <div>
                                                <span className="fw-bold d-block text-dark">{c.firstName} {c.lastName}</span>
                                                <div className="small d-flex gap-2 align-items-center mt-1">
                                                    <span className={`badge rounded-pill ${c.sex === 'male' ? 'bg-light' : c.sex === 'female' ? 'bg-danger' : 'bg-secondary'} bg-opacity-10 text-${c.sex === 'male' ? 'primary' : c.sex === 'female' ? 'danger' : 'muted'}`} style={{ fontSize: '10px' }}>
                                                        <i className={`fas ${c.sex === 'male' ? 'fa-mars' : c.sex === 'female' ? 'fa-venus' : 'fa-question'} me-1`}></i>
                                                        {c.sex === 'male' ? 'Uomo' : c.sex === 'female' ? 'Donna' : 'Non indicato'}
                                                    </span>
                                                    <span className="text-muted" style={{ fontSize: '11px' }}>
                                                        <i className="fas fa-birthday-cake me-1 opacity-50"></i>
                                                        {c.birthDate 
                                                            ? `${new Date().getFullYear() - new Date(c.birthDate).getFullYear()} anni`
                                                            : c.latestAge ? `${c.latestAge} anni` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="small">
                                            <div className="mb-1">
                                                <a href={`mailto:${c.email}`} className="text-decoration-none text-primary d-flex align-items-center fw-medium">
                                                    <i className="fas fa-envelope text-muted me-2" style={{ width: '16px' }}></i>{c.email}
                                                </a>
                                            </div>
                                            {c.phone && (
                                                <div>
                                                    <a href={`tel:${c.phone}`} className="text-decoration-none text-dark d-flex align-items-center">
                                                        <i className="fas fa-phone text-muted me-2" style={{ width: '16px' }}></i>{c.phone}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {c.city?.name ? (
                                            <span className="small text-muted">
                                                <i className="fas fa-map-marker-alt me-2 opacity-50"></i>
                                                {c.city.name}
                                            </span>
                                        ) : (
                                            <span className="text-muted opacity-25 small">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="small">
                                            {c.instagram && <div className="mb-1"><i className="fab fa-instagram text-danger me-2" style={{ width: '16px' }}></i>{c.instagram}</div>}
                                            {c.facebook && <div><i className="fab fa-facebook text-primary me-2" style={{ width: '16px' }}></i>{c.facebook}</div>}
                                            {!c.instagram && !c.facebook && <span className="text-muted opacity-50 italic small">Nessun social</span>}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        {c.notificationsDisabled ? (
                                            <span className="badge rounded-pill bg-danger px-3 py-2 shadow-sm" title="Notifiche disabilitate">
                                                <i className="fas fa-bell-slash me-1"></i> No
                                            </span>
                                        ) : (
                                            <span className="badge rounded-pill bg-success bg-opacity-10 text-success px-3 py-2 shadow-sm" title="Notifiche attive">
                                                <i className="fas fa-bell me-1"></i> Sì
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <span className="badge rounded-pill bg-primary px-3 py-2 fw-bold shadow-sm">
                                            {c.eventCount} {c.eventCount === 1 ? 'evento' : 'eventi'}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <button 
                                            className="btn btn-sm btn-outline-danger rounded-pill px-3" 
                                            onClick={() => handleForgetContact(c.email)}
                                            title="Diritto all'oblio (elimina tutto)"
                                        >
                                            <i className="fas fa-trash-alt"></i> Oblio
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
